/**
 * 充值逻辑 Hook
 */
import { computed, ref } from 'vue'
import type { Book } from '../configs/types'
import { PRODUCT_LIST_API, PaymentMethodEnum, STRIPE_PAY_MODE } from '../constants'
import {
  createAvailablePaymentMethods,
  createSelectPaymentMethod,
  getPayType,
  getPaymentChannelName,
  getTopupPaymentApi,
  initializePaymentMethod,
} from './paymentUtils'
import { useStripeCheckout } from './useStripeCheckout'
import { api } from '@/api'
import { $formatErrorMessage } from '@/helpers'
import { useLoadingStore, useToastStore, useUserStore } from '@/stores'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { useFbqInitiateCheckout } from '@/composables/fbq'
import { useAxonBeginCheckout } from '@/composables/axon/useAxonBeginCheckout'

interface UsePurchaseOptions {
  /** Stripe 内嵌支付成功后是否整页重载（默认 false：只刷新用户余额，不重载） */
  reloadOnStripeSuccess?: boolean
}

export function usePurchase(Scenario: string, book?: Book, options?: UsePurchaseOptions) {
  const reloadOnStripeSuccess = options?.reloadOnStripeSuccess ?? false
  const data = ref<any>({})
  const PayTemplateSuccess = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // 多支付方式相关状态
  const currentPaymentMethod = ref<number>(PaymentMethodEnum.PAYPAL) // 默认 PayPal
  const productLists = ref<any[]>([]) // 按支付方式分组的商品列表
  const hasPayPalPasswordlessPayment = ref(false)
  const hasPlacedOrder = ref(false)

  const { $toast } = useToastStore()
  const { $showLoading, $hideLoading } = useLoadingStore()
  const { elementClickForPay } = useElementClick()

  // FB 原生埋点（仅 InitiateCheckout）
  const { reportInitiateCheckout } = useFbqInitiateCheckout()

  // Axon 埋点（begin_checkout；purchase 统一由 /paying 验单成功后上报）
  const { reportBeginCheckout } = useAxonBeginCheckout()

  // Stripe 内嵌支付（仅 Stripe 走内嵌弹窗，PayPal 维持跳转）
  const {
    stripeClientSecret,
    stripeVisible,
    openStripeCheckout,
    closeStripeCheckout,
    setStripeVisible,
    verifyStripeReceipt,
  } = useStripeCheckout()

  /**
   * 根据支付方式和商品类型获取支付 API
   */
  function getPaymentApi(paymentMethod: number): string {
    return getTopupPaymentApi(paymentMethod)
  }

  /**
   * 初始化支付方式
   */
  function initPaymentMethod() {
    initializePaymentMethod(
      currentPaymentMethod,
      productLists,
      hasPayPalPasswordlessPayment,
      hasPlacedOrder,
    )
  }

  /**
   * 可用的支付方式列表（充值页面排除 Stripe）
   */
  const availablePaymentMethods = computed(createAvailablePaymentMethods(
    productLists,
    hasPayPalPasswordlessPayment,
    hasPlacedOrder,
  ))
  /**
   * 当前支付方式的商品列表（充值商品，无 cycle 或 cycle=0）
   *
   * 从 productLists 中找到对应支付方式的商品列表，然后过滤出充值商品
   */
  const currentPaymentProducts = computed(() => {
    const currentMethod = Number(currentPaymentMethod.value)

    // 从 productLists 中找到对应支付方式的商品列表
    const productList = availablePaymentMethods.value.find(
      list => Number(list.paymentMethod) === currentMethod,
    )

    if (!productList || !productList.products)
      return []

    const products = productList.products

    // 兜底：如果没有 defaultDisplay=1 的商品（即都为 0），展示所有商品
    return products
  })

  /**
   * templateProducts 兼容旧代码，使用 currentPaymentProducts
   */
  const templateProducts = computed(() => currentPaymentProducts.value)

  const getTopUpList = async function () {
    loading.value = true
    error.value = null
    try {
      const res: any = await api.get<any>(PRODUCT_LIST_API.TOPUP, {
        params: {
          type: book?.contentId ? 1 : 2,
          contentId: book?.contentId || '',
          contentChapterId: book?.contentChapterId || '',
        },
      })
      data.value = res.data || {}

      // 解析多支付方式数据
      if (res.data && typeof res.data === 'object') {
        hasPayPalPasswordlessPayment.value = res.data.hasPayPalPasswordlessPayment || false
        hasPlacedOrder.value = res.data.hasPlacedOrder || false
        productLists.value = res.data.productLists || []

        // 初始化默认支付方式
        initPaymentMethod()
      }
    }
    catch (err) {
      const errorMsg = $formatErrorMessage(err)
      error.value = errorMsg
      $toast(errorMsg)
    }
    loading.value = false
  }

  const isReloadPage = function () {
    // 无数据时的处理逻辑
    const currentUrl = new URL(window.location.href)
    if (book?.contentId) {
      // 在阅读页面时，移除 isShowpopupRecharge 参数后刷新页面，更新章节解锁状态
      currentUrl.searchParams.delete('isShowpopupRecharge')
      window.location.href = currentUrl.href
    }
    else {
      window.location.reload()
    }
  }

  const PayTemplate = async function (item: any) {
    elementClickForPay('purchase', {
      purchaseId: item.payConfigId,
      commodityId: item.productId,
      purchaseType: '1',
      price: item.price,
      paymentChannel: getPaymentChannelName(currentPaymentMethod.value),
      purchaseScenario: Scenario, // store chapter
    })

    // 创建订单时生成 InitiateCheckout eventId，随下单接口透传后端做双端去重
    const fbEventId = reportInitiateCheckout({
      value: Number(item.price) || 0,
      currency: 'USD',
      contentIds: [String(item.productId)],
      contentName: String(item.name || ''),
      contentCategory: 'product',
      dedup: false,
    })

    // Axon：下单呼出支付网关时上报 begin_checkout（item_name=总钻石数，含赠币）
    const coinsTotal = (Number(item.coinsBuy) || 0) + (Number(item.coinsBonus) || 0)
    const axonItemName = `${coinsTotal} Diamonds`
    reportBeginCheckout({
      item_id: item.productId,
      item_name: axonItemName,
      price: item.price,
    })

    $showLoading()
    const url = `${window.location.origin}/paying?redirect_source=${encodeURIComponent(window.location.href)}&paymentChannel=${getPaymentChannelName(currentPaymentMethod.value)}&axon_price=${item.price}&axon_product_id=${item.productId}&axon_item_name=${encodeURIComponent(axonItemName)}`
    const params = {
      payConfigId: item.payConfigId,
      productId: item.productId,
      contentId: book?.contentId,
      contentChapterId: book?.contentChapterId,
      memberCurrencyCode: 'usd',
      payType: getPayType(currentPaymentMethod.value),
      type: book?.contentId ? 1 : 2, // 1: 阅读页面, 2: store
      // extendProductId 用于后端创建 Session 及 /paying 跳转回来后的验单
      extendProductId: item.extendProductId,
      successUrl: `${url}&payment_status=success`,
      cancelUrl: `${url}&payment_status=cancel`,
      ...(fbEventId ? { fbEventId } : {}),
    }

    // Stripe 内嵌模式（版本3，需注册域名）才走弹窗；
    // hosted 模式（版本1）下 Stripe 与 PayPal 共用下方整页跳转逻辑（跳转 Stripe 官方收银台 → /paying 验单 → 跳回）。
    if (currentPaymentMethod.value === PaymentMethodEnum.STRIPE && STRIPE_PAY_MODE === 'embedded') {
      try {
        // extendProductId 来自商品信息，验单时需回传
        await openStripeCheckout(params, item.extendProductId)
      }
      catch (error) {
        console.error('[STRIPE] openStripeCheckout 失败:', error)
        $toast($formatErrorMessage(error))
      }
      $hideLoading()
      return
    }

    // PayPal / Stripe(hosted) → 整页跳转逻辑
    const apiEndpoint = getPaymentApi(currentPaymentMethod.value)

    try {
      const { data } = await api.post<any>(apiEndpoint, params)
      PayTemplateSuccess.value = true
      if (data) {
        // 判断 data 是否为有效的 URL
        try {
          const _ = new URL(data)
          window.location.href = data
        }
        catch {
          isReloadPage()
        }
      }
      else {
        // 无数据时的处理逻辑
        isReloadPage()
      }
    }
    catch (error) {
      $toast($formatErrorMessage(error))
    }
    $hideLoading()
  }

  /**
   * 内嵌支付完成（onComplete）：验单成功后刷新页面（更新余额/章节解锁状态）
   * 注：游戏 iframe 场景不走此处（由游戏页直接用 useStripeCheckout + sendCommandToGame）
   */
  const onStripeComplete = async function () {
    $showLoading()
    const ok = await verifyStripeReceipt()
    $hideLoading()
    closeStripeCheckout()
    if (ok) {
      PayTemplateSuccess.value = true
      if (reloadOnStripeSuccess) {
        isReloadPage()
      }
      else {
        // 默认不重载，仅刷新用户信息（余额等）
        useUserStore().init({ force: true })
      }
    }
    else {
      console.error('[STRIPE] 验单失败 verifyStripeReceipt 返回 false')
      $toast('Payment verification failed')
    }
  }

  /**
   * 用户主动关闭内嵌弹窗（取消支付）
   */
  const onStripeCancel = function () {
    closeStripeCheckout()
  }

  /**
   * 切换支付方式
   */
  const selectPaymentMethod = createSelectPaymentMethod(currentPaymentMethod)

  getTopUpList()

  return {
    templateProducts,
    data,
    PayTemplateSuccess,
    loading,
    PayTemplate,
    // 多支付方式相关
    currentPaymentMethod,
    availablePaymentMethods,
    currentPaymentProducts,
    selectPaymentMethod,
    productLists,
    hasPayPalPasswordlessPayment,
    hasPlacedOrder,
    // Stripe 内嵌支付
    stripeClientSecret,
    stripeVisible,
    setStripeVisible,
    onStripeComplete,
    onStripeCancel,
  }
}
